import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Box, Card, Stack, TextField, MenuItem,
    Button, Grid, LinearProgress, Typography, Alert, Snackbar,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    FormControlLabel, Checkbox, Select, FormControl, InputLabel
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';

const CalculationBases = ({ setIsLoggedIn }) => {
    const [loading, setLoading] = useState(false);
    const [rowData, setRowData] = useState([]);
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        basis_code: '',
        basis_name: '',
        basis_type: 'MONTHLY_FIXED_DAYS',
        fixed_days_per_month: 26,
        hours_per_day: 8,
        description: '',
        is_active: 1
    });

    const basisTypes = [
        { value: 'YEARLY', label: 'Yearly' },
        { value: 'MONTHLY_FIXED_DAYS', label: 'Monthly (Fixed Days)' },
        { value: 'MONTHLY_ACTUAL_DAYS', label: 'Monthly (Actual Days in Month)' },
        { value: 'PAY_CYCLE_DAYS', label: 'Pay Cycle Actual Days' },
        { value: 'HOURLY', label: 'Hourly' }
    ];

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/calc-bases`, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setRowData(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching calculation bases:", error);
            setStatusMsg({ open: true, msg: 'Error fetching data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                basis_code: item.basis_code,
                basis_name: item.basis_name,
                basis_type: item.basis_type,
                fixed_days_per_month: item.fixed_days_per_month || 0,
                hours_per_day: item.hours_per_day || 8,
                description: item.description || '',
                is_active: item.is_active
            });
        } else {
            setEditingItem(null);
            setFormData({
                basis_code: '',
                basis_name: '',
                basis_type: 'MONTHLY_FIXED_DAYS',
                fixed_days_per_month: 26,
                hours_per_day: 8,
                description: '',
                is_active: 1
            });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const url = editingItem
                ? `${process.env.REACT_APP_API_URL1}/sm/payroll/calc-bases/${editingItem.id}`
                : `${process.env.REACT_APP_API_URL1}/sm/payroll/calc-bases`;

            const method = editingItem ? 'put' : 'post';

            const response = await axios[method](url, formData, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });

            if (response.data.success) {
                setStatusMsg({ open: true, msg: editingItem ? 'Updated successfully' : 'Created successfully', severity: 'success' });
                setDialogOpen(false);
                fetchData();
            }
        } catch (error) {
            console.error("Error saving calculation basis:", error);
            setStatusMsg({ open: true, msg: 'Error saving data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this calculation basis?")) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const response = await axios.delete(`${process.env.REACT_APP_API_URL1}/sm/payroll/calc-bases/${id}`, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setStatusMsg({ open: true, msg: 'Deleted successfully', severity: 'success' });
                fetchData();
            }
        } catch (error) {
            console.error("Error deleting calculation basis:", error);
            setStatusMsg({ open: true, msg: 'Error deleting data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const columnDefs = [
        { field: "basis_code", headerName: "Code", width: 120 },
        { field: "basis_name", headerName: "Name", width: 200 },
        { field: "basis_type", headerName: "Type", width: 220 },
        { field: "fixed_days_per_month", headerName: "Fixed Days", width: 130 },
        { field: "hours_per_day", headerName: "Hrs/Day", width: 120 },
        { field: "is_active", headerName: "Status", width: 100, cellRenderer: p => p.value ? 'Active' : 'Inactive' },
        {
            headerName: "Actions",
            width: 150,
            cellRenderer: p => (
                <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
                    <IconButton size="small" color="primary" onClick={() => handleOpenDialog(p.data)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(p.data.id)}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Stack>
            )
        }
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f4f7fe' }}>
            <AppHeader title="Calculation Bases" setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: 3, flexGrow: 1, overflow: 'hidden' }}>
                <Card sx={{ p: 2, mb: 2, borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight="bold">Master Data: Calculation Bases</Typography>
                        <Stack direction="row" spacing={2}>
                            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData}>Refresh</Button>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} sx={{ bgcolor: '#1a237e' }}>
                                Add New Basis
                            </Button>
                        </Stack>
                    </Stack>
                </Card>

                {loading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

                <Card sx={{ height: 'calc(100% - 130px)', borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                    <Box className="ag-theme-quartz" sx={{ height: '100%', width: '100%' }}>
                        <AgGridReact
                            rowData={rowData}
                            columnDefs={columnDefs}
                            defaultColDef={{ sortable: true, filter: true, resizable: true }}
                        />
                    </Box>
                </Card>
            </Box>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingItem ? 'Edit Calculation Basis' : 'Add New Calculation Basis'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth label="Code" size="small"
                                value={formData.basis_code}
                                onChange={e => setFormData({ ...formData, basis_code: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth label="Name" size="small"
                                value={formData.basis_name}
                                onChange={e => setFormData({ ...formData, basis_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Basis Type</InputLabel>
                                <Select
                                    label="Basis Type"
                                    value={formData.basis_type}
                                    onChange={e => setFormData({ ...formData, basis_type: e.target.value })}
                                >
                                    {basisTypes.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth label="Fixed Days/Month" type="number" size="small"
                                value={formData.fixed_days_per_month}
                                onChange={e => setFormData({ ...formData, fixed_days_per_month: e.target.value })}
                                disabled={formData.basis_type === 'MONTHLY_ACTUAL_DAYS' || formData.basis_type === 'PAY_CYCLE_DAYS'}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth label="Hours/Day" type="number" size="small"
                                value={formData.hours_per_day}
                                onChange={e => setFormData({ ...formData, hours_per_day: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth label="Description" multiline rows={2} size="small"
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={formData.is_active === 1} onChange={e => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })} />}
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} variant="outlined">Cancel</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#1a237e' }}>Save</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={statusMsg.open}
                autoHideDuration={6000}
                onClose={() => setStatusMsg({ ...statusMsg, open: false })}
            >
                <Alert severity={statusMsg.severity} sx={{ width: '100%' }}>{statusMsg.msg}</Alert>
            </Snackbar>
        </Box>
    );
};

export default CalculationBases;
